<script setup lang="ts">
import { languageIdBus } from '@/event/languageIdBus'
import { productBus } from '@/event/product/productBus'
import { relatedGroupBus } from '@/event/product/relatedGroupeBus'
import { forceSelectOptionHappy } from '@/happyTheme/event/product/forceSelectOptionHappy'
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { type SelectedOptionValueHappy } from '@/happyTheme/interfaces/product/selectedOptionInterface'
import type { Option } from '@/storage/products'
import { getOptionName } from '@/utils/product/getOptionName'
import { getOptionValueName } from '@/utils/product/getOptionValueName'
import { getOptionValueNameById } from '@/utils/product/getOptionValueNameById'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
    maxQuantity?: number
    currentQuantity: number
    option: Option
    currentOptionValueId: number
}>()

const counterOptionValue = ref(props.currentQuantity)

watch(
    () =>
        [
            selectOptionsListHappy.value[props.option.option_id]!,
            showSelectedModalProductHappy.value
        ] as const,
    ([newValue, newShow]) => {
        if (!newValue) return
        // selectedValues.value = newValue

        if (!selectOptionsListHappy.value[props.option.option_id]) return

        const optionValueFindReset = [
            ...selectOptionsListHappy.value[props.option.option_id].selected_values
                .checkbox_option.option_value_free,
            ...selectOptionsListHappy.value[props.option.option_id].selected_values
                .checkbox_option.option_value_paid
        ].filter((o) => o.option_value_reset === 1)

        const optionValueFind = props.option.values.find(
            (e) => e.option_value_id === props.currentOptionValueId
        )

        if (
            props.option.type === 'radio' &&
            selectOptionsListHappy.value[props.option.option_id].selected_values
                .radio_option.option_value_id !== props.currentOptionValueId &&
            selectOptionsListHappy.value[props.option.option_id].selected_values
                .radio_option.option_value_id !== null
        ) {
            counterOptionValue.value = 0
            return
        } else if (
            (props.option.type === 'select' || props.option.type === 'checkbox') &&
            optionValueFindReset.length !== 0
        ) {
            const hasReset = optionValueFindReset.some((element) => {
                counterOptionValue.value = 1
                return (
                    element.option_value_reset === 1 &&
                    element.option_value_id === optionValueFind?.option_value_id
                )
            })

            if (hasReset) {
                counterOptionValue.value = 1
                return
            }
        }

        counterOptionValue.value = props.currentQuantity
    },
    { deep: true, immediate: true }
)

const ShowButtonPlus = computed(() => {
    const currentOption = props.option
    const type = currentOption.type
    const currentOptionValue = currentOption.values.find(
        (e) => e.option_value_id === props.currentOptionValueId
    )
    const typeSelect = type === 'select' || type === 'checkbox'

    if (
        type === 'radio' &&
        selectOptionsListHappy.value[props.option.option_id].selected_values.radio_option
            .option_value_id !== props.currentOptionValueId
    ) {
        counterOptionValue.value = 0
        return true
    } else if (typeSelect) {
        const findFreeReset = selectOptionsListHappy.value[
            props.option.option_id
        ].selected_values.checkbox_option.option_value_free.find(
            (r) => r.option_value_id === props.currentOptionValueId
        )
        const findPaidReset = selectOptionsListHappy.value[
            props.option.option_id
        ].selected_values.checkbox_option.option_value_paid.find(
            (r) => r.option_value_id === props.currentOptionValueId
        )

        if (currentOptionValue?.reset === 1 && !findFreeReset && !findPaidReset) {
            return true
        }

        if (selectOptionsListHappy.value[props.option.option_id].selected_values) {
            const selectedIds = [
                ...selectOptionsListHappy.value[props.option.option_id].selected_values
                    .checkbox_option.option_value_free,
                ...selectOptionsListHappy.value[props.option.option_id].selected_values
                    .checkbox_option.option_value_paid
            ]
            if (
                typeSelect &&
                props.maxQuantity !== selectedIds.length &&
                currentOptionValue?.reset === 0
            ) {
                return true
            }
        }
    }
})

const emit = defineEmits<{
    (e: 'quantityOptionValue', quantityOptionValue: number, action: string): void
}>()

function increment(optionValueId: number, optionId: number) {
    if (
        props.maxQuantity &&
        counterOptionValue.value >= props.maxQuantity &&
        productBus.value?.options
    ) {
        return
    }

    const optionFound = productBus.value?.options.find((e) => e.option_id === optionId)
    if (!optionFound) return

    const freeOptionCount = optionFound.free_option_count || 0
    const maxOptionCount = optionFound.max_option_count || 0

    const optionValueFind = optionFound?.values.find(
        (e) => e.option_value_id === optionValueId
    )
    if (!optionValueFind) return

    counterOptionValue.value++

    if (optionValueFind && optionFound) {
        if (optionFound.type === 'radio') {
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option = {
                option_value_free: [],
                option_value_paid: [],
                quantity_free: freeOptionCount || 0,
                quantity_paid: Math.max(0, maxOptionCount - freeOptionCount)
            }

            const currentOptionAdded = {
                option_value_id: optionValueFind.option_value_id,
                option_value_image: optionValueFind.image,
                option_value_name: optionValueFind.language[2].name,
                option_value_price: optionValueFind.ov_price
            }

            selectOptionsListHappy.value[optionId].selected_values.radio_option =
                currentOptionAdded

            optionFound.values.forEach((oF) => {
                if (oF.related_option_id.length === 0) return
                oF.related_option_id.forEach((roi) => {
                    if (!relatedGroupBus.value.includes(roi)) {
                        relatedGroupBus.value.push(roi)
                    }
                })
            })

            if (optionValueFind.related_option_id.length !== 0) {
                optionValueFind.related_option_id.forEach((roi) => {
                    const idx = relatedGroupBus.value.indexOf(roi)
                    if (idx !== -1) {
                        relatedGroupBus.value.splice(idx, 1)
                    }
                })
            }
        }

        if (optionFound.type === 'select' || optionFound.type === 'checkbox') {
            const selectedvalueFree =
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                    .option_value_free
            const selectedvaluePaid =
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                    .option_value_paid

            if (optionValueFind.reset === 1) {
                const keepOptionsFree = selectedvalueFree.filter(
                    (ov) => ov.option_value_grup !== optionValueFind.grup
                )
                const keepOptionsPaid = selectedvaluePaid.filter(
                    (ov) => ov.option_value_grup !== optionValueFind.grup
                )

                selectOptionsListHappy.value[
                    optionId
                ].selected_values.checkbox_option.option_value_free = keepOptionsFree
                selectOptionsListHappy.value[
                    optionId
                ].selected_values.checkbox_option.option_value_paid = keepOptionsPaid
            } else {
                const existingReset = [...selectedvalueFree, ...selectedvaluePaid].find(
                    (item) =>
                        item.option_value_reset &&
                        item.option_value_grup === optionValueFind.grup
                )

                if (existingReset) {
                    const freeIndex = selectedvalueFree.findIndex(
                        (item) => item.option_value_id === existingReset.option_value_id
                    )
                    if (freeIndex !== -1) {
                        selectedvalueFree.splice(freeIndex, 1)
                    }

                    const paidIndex = selectedvaluePaid.findIndex(
                        (item) => item.option_value_id === existingReset.option_value_id
                    )

                    if (paidIndex !== -1) {
                        selectOptionsListHappy.value[
                            optionId
                        ].selected_values.checkbox_option.option_value_paid.splice(
                            paidIndex,
                            1
                        )
                    }
                    selectOptionsListHappy.value[
                        optionId
                    ].selected_values.checkbox_option.quantity_free = freeOptionCount
                    selectOptionsListHappy.value[
                        optionId
                    ].selected_values.checkbox_option.quantity_paid =
                        maxOptionCount - freeOptionCount
                }
            }

            const oneIsFree = selectedvalueFree.findIndex(
                (i) => i.option_value_one_is_free === true
            )

            if (oneIsFree !== -1) {
                const [oneIsFreeItem] = selectOptionsListHappy.value[
                    optionId
                ].selected_values.checkbox_option.option_value_free.splice(oneIsFree, 1)

                const oneIsFreeFound = props.option.values.find(
                    (i) => i.option_value_id === oneIsFreeItem.option_value_id
                )
                if (!oneIsFreeFound) return

                const currentOptionAdded = {
                    option_value_id: oneIsFreeFound?.option_value_id,
                    option_value_name: getOptionValueNameById(
                        oneIsFreeFound.option_value_id,
                        languageIdBus.value
                    ),
                    option_value_image: oneIsFreeFound.image,
                    option_value_price: oneIsFreeFound.ov_price,
                    option_value_reset: oneIsFreeFound.reset,
                    option_value_grup: oneIsFreeFound.grup,
                    option_value_one_is_free: true,
                    option_value_special: false
                }

                selectOptionsListHappy.value[
                    optionId
                ].selected_values.checkbox_option.option_value_paid.push(
                    currentOptionAdded
                )
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                    .quantity_paid--
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                    .quantity_free++
            }

            const quantityFree = Number(
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                    .quantity_free
            )
            const quantityPaid = Number(
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                    .quantity_paid
            )
            // console.log(selectOptionsListHappy.value[optionId])

            // console.log(optionValueFind)
            switch (optionValueFind.price_status) {
                case 'general':
                    // paid
                    if (quantityFree === 0 && quantityPaid === 0) {
                        const currentOptionAdded = {
                            option_value_id: optionValueFind?.option_value_id,
                            option_value_name: getOptionValueNameById(
                                optionValueFind.option_value_id,
                                languageIdBus.value
                            ),
                            option_value_image: optionValueFind.image,
                            option_value_price: optionValueFind.ov_price,
                            option_value_reset: optionValueFind.reset,
                            option_value_grup: optionValueFind.grup,
                            option_value_one_is_free: false,
                            option_value_special: false
                        }
                        selectOptionsListHappy.value[
                            optionId
                        ].selected_values.checkbox_option.option_value_paid.push(
                            currentOptionAdded
                        )
                    }
                    // paid
                    if (quantityFree === 0 && quantityPaid !== 0) {
                        const currentOptionAdded = {
                            option_value_id: optionValueFind?.option_value_id,
                            option_value_name: getOptionValueNameById(
                                optionValueFind.option_value_id,
                                languageIdBus.value
                            ),
                            option_value_image: optionValueFind.image,
                            option_value_price: optionValueFind.ov_price,
                            option_value_reset: optionValueFind.reset,
                            option_value_grup: optionValueFind.grup,
                            option_value_one_is_free: false,
                            option_value_special: false
                        }
                        selectOptionsListHappy.value[
                            optionId
                        ].selected_values.checkbox_option.option_value_paid.push(
                            currentOptionAdded
                        )
                        selectOptionsListHappy.value[optionId].selected_values
                            .checkbox_option.quantity_paid--
                    }
                    // free => paid
                    // console.log(quantityFree)
                    if (quantityFree !== 0 && quantityPaid !== 0) {
                        if (selectedvalueFree.length <= quantityFree) {
                            const currentOptionAdded = {
                                option_value_id: optionValueFind?.option_value_id,
                                option_value_name: getOptionValueNameById(
                                    optionValueFind.option_value_id,
                                    languageIdBus.value
                                ),
                                option_value_image: optionValueFind.image,
                                option_value_price: '0',
                                option_value_reset: optionValueFind.reset,
                                option_value_grup: optionValueFind.grup,
                                option_value_one_is_free: false,
                                option_value_special: false
                            }
                            selectOptionsListHappy.value[
                                optionId
                            ].selected_values.checkbox_option.option_value_free.push(
                                currentOptionAdded
                            )
                            selectOptionsListHappy.value[optionId].selected_values
                                .checkbox_option.quantity_free--
                        } else {
                            const currentOptionAdded = {
                                option_value_id: optionValueFind?.option_value_id,
                                option_value_name: getOptionValueNameById(
                                    optionValueFind.option_value_id,
                                    languageIdBus.value
                                ),
                                option_value_image: optionValueFind.image,
                                option_value_price: optionValueFind.ov_price,
                                option_value_reset: optionValueFind.reset,
                                option_value_grup: optionValueFind.grup,
                                option_value_one_is_free: false,
                                option_value_special: false
                            }
                            selectOptionsListHappy.value[
                                optionId
                            ].selected_values.checkbox_option.option_value_paid.push(
                                currentOptionAdded
                            )
                            selectOptionsListHappy.value[optionId].selected_values
                                .checkbox_option.quantity_paid--
                        }
                    }

                    if (quantityFree !== 0 && quantityPaid === 0) {
                        const currentOptionAdded = {
                            option_value_id: optionValueFind?.option_value_id,
                            option_value_name: getOptionValueNameById(
                                optionValueFind.option_value_id,
                                languageIdBus.value
                            ),
                            option_value_image: optionValueFind.image,
                            option_value_price: optionValueFind.ov_price,
                            option_value_reset: optionValueFind.reset,
                            option_value_grup: optionValueFind.grup,
                            option_value_one_is_free: false,
                            option_value_special: false
                        }
                        selectOptionsListHappy.value[
                            optionId
                        ].selected_values.checkbox_option.option_value_free.push(
                            currentOptionAdded
                        )
                        selectOptionsListHappy.value[optionId].selected_values
                            .checkbox_option.quantity_free--
                    }
                    break
                case 'one_is_free':
                    // if (selectedvalueFree.length > 1) {
                    // }

                    if (selectedvalueFree.length === 0) {
                        const currentOptionAdded = {
                            option_value_id: optionValueFind?.option_value_id,
                            option_value_name: getOptionValueNameById(
                                optionValueFind.option_value_id,
                                languageIdBus.value
                            ),
                            option_value_image: optionValueFind.image,
                            option_value_price: '0',
                            option_value_reset: optionValueFind.reset,
                            option_value_grup: optionValueFind.grup,
                            option_value_one_is_free: true,
                            option_value_special: false
                        }
                        selectOptionsListHappy.value[
                            optionId
                        ].selected_values.checkbox_option.option_value_free.push(
                            currentOptionAdded
                        )
                        selectOptionsListHappy.value[optionId].selected_values
                            .checkbox_option.quantity_free--
                    } else {
                        const currentOptionAdded = {
                            option_value_id: optionValueFind?.option_value_id,
                            option_value_name: getOptionValueNameById(
                                optionValueFind.option_value_id,
                                languageIdBus.value
                            ),
                            option_value_image: optionValueFind.image,
                            option_value_price: optionValueFind.ov_price,
                            option_value_reset: optionValueFind.reset,
                            option_value_grup: optionValueFind.grup,
                            option_value_one_is_free: true,
                            option_value_special: false
                        }
                        selectOptionsListHappy.value[
                            optionId
                        ].selected_values.checkbox_option.option_value_paid.push(
                            currentOptionAdded
                        )
                        selectOptionsListHappy.value[optionId].selected_values
                            .checkbox_option.quantity_paid--
                    }
                    break
                case 'special':
                    const currentOptionAdded = {
                        option_value_id: optionValueFind?.option_value_id,
                        option_value_name: getOptionValueNameById(
                            optionValueFind.option_value_id,
                            languageIdBus.value
                        ),
                        option_value_image: optionValueFind.image,
                        option_value_price: optionValueFind.ov_price,
                        option_value_reset: optionValueFind.reset,
                        option_value_grup: optionValueFind.grup,
                        option_value_one_is_free: false,
                        option_value_special: true
                    }

                    selectOptionsListHappy.value[
                        optionId
                    ].selected_values.checkbox_option.option_value_paid.push(
                        currentOptionAdded
                    )
                    selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                        .quantity_paid--
                    break
            }
        }
    }
}

function decrement(optionValueId: number, optionId: number) {
    forceSelectOptionHappy.value = false
    if (counterOptionValue.value > 0) {
        const propsOption = props.option
        const optionValueFind = props.option.values.find(
            (e) => e.option_value_id === optionValueId
        )

        if (propsOption.type === 'radio' && optionValueFind) {
            selectOptionsListHappy.value[
                props.option.option_id
            ].selected_values.radio_option = {
                option_value_id: 0,
                option_value_name: '',
                option_value_image: '',
                option_value_price: ''
            }

            propsOption.values.forEach((oF) => {
                if (oF.related_option_id.length === 0) return
                oF.related_option_id.forEach((roi) => {
                    if (!relatedGroupBus.value.includes(roi)) {
                        relatedGroupBus.value.push(roi)
                    }
                })
            })
            counterOptionValue.value--
            return
        }

        if (propsOption.type === 'select' || propsOption.type === 'checkbox') {
            const optionValueSelected =
                selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            const optionValueFreeSelected = optionValueSelected.option_value_free
            const optionValuePaidSelected = optionValueSelected.option_value_paid

            const optionValueFreeIndex = optionValueFreeSelected.findIndex(
                (i) => i.option_value_id === optionValueId
            )
            const optionValuePaidIndex = optionValuePaidSelected.findIndex(
                (i) => i.option_value_id === optionValueId
            )

            console.log(optionValuePaidIndex)

            if (optionValuePaidIndex !== -1) {
                optionValuePaidSelected.splice(optionValuePaidIndex, 1)
                if (
                    props.option.free_option_count !== 0 &&
                    props.option.max_option_count === 0
                ) {
                    optionValueSelected.quantity_paid++
                }
                counterOptionValue.value--
                return
            }

            console.log(optionValueFreeIndex)

            if (optionValueFreeIndex !== -1) {
                optionValueFreeSelected.splice(optionValueFreeIndex, 1)

                if (optionValuePaidSelected.length === 0) {
                    optionValueSelected.quantity_free++
                    // return
                } else {
                    optionValuePaidSelected.forEach((e, idx) => {
                        if (
                            e.option_value_one_is_free !== true &&
                            e.option_value_special !== true &&
                            optionValuePaidSelected.length >= 1
                        ) {
                            optionValuePaidSelected.splice(idx, 1)

                            optionValueFreeSelected.push({
                                option_value_id: e.option_value_id,
                                option_value_name: e.option_value_name,
                                option_value_image: e.option_value_image,
                                option_value_price: '0',
                                option_value_reset: 0,
                                option_value_grup: '',
                                option_value_one_is_free: false,
                                option_value_special: false
                            })
                            optionValueSelected.quantity_paid++
                            return
                        } else if (
                            optionValueFreeSelected.length === 0 &&
                            optionValuePaidSelected.length === 1 &&
                            e.option_value_one_is_free === true &&
                            e.option_value_special !== true
                        ) {
                            optionValuePaidSelected.splice(idx, 1)

                            optionValueFreeSelected.push({
                                option_value_id: e.option_value_id,
                                option_value_name: e.option_value_name,
                                option_value_image: e.option_value_image,
                                option_value_price: '0',
                                option_value_reset: 0,
                                option_value_grup: '',
                                option_value_one_is_free: true,
                                option_value_special: false
                            })
                            optionValueSelected.quantity_paid++
                        } else {
                            optionValueSelected.quantity_free++
                        }
                    })
                }
                return
            }
        }
    }
}
</script>

<template>
    <section class="section-button-select-option-value-happy">
        <button
            class=""
            :class="{
                'button-minus-option-value-happy': counterOptionValue > 0,
                hidden: counterOptionValue <= 0
            }"
            @click.stop="decrement(props.currentOptionValueId, props.option.option_id)"
        >
            <svg
                class="svg-minus-happy"
                viewBox="0 0 15 2"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M1 1H14" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>
        <p
            class=""
            :class="{
                'text-counter-option-value-happy': counterOptionValue > 0,
                hidden: counterOptionValue <= 0
            }"
        >
            {{ counterOptionValue }}
        </p>

        <button
            class="button-plus-option-value-happy"
            @click.stop="increment(props.currentOptionValueId, props.option.option_id)"
            v-if="ShowButtonPlus"
        >
            <svg
                class="svg-plus-happy"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M1 8.5H16M8.5 1V16"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>
    </section>
</template>

<style scoped>
.section-button-select-option-value-happy {
    @apply min-w-24 flex items-center justify-end gap-2;
}

.button-minus-option-value-happy {
    @apply flex items-center border border-button-yellow-happy-light rounded p-1;
}
.svg-minus-happy {
    @apply w-4 h-4 stroke-button-yellow-happy-light;
}

.text-counter-option-value-happy {
    @apply happy-light:text-text-dark-happy-light happy-dark:text-bg-happy-light text-lg text-center align-baseline w-6 font-semibold;
}

.button-plus-option-value-happy {
    @apply flex items-center border-button-yellow-happy-light bg-button-yellow-happy-light p-1 rounded;
}

.svg-plus-happy {
    @apply w-4 h-4 stroke-text-dark-happy-light;
}
.hidden {
    display: none;
}

.button-minus-option-value-happy:active .svg-minus-happy {
    @apply stroke-bg-happy-light;
}
.button-minus-option-value-happy:active,
.button-plus-option-value-happy:active {
    @apply bg-hover-yellow-happy border-hover-yellow-happy;
}
@media (hover: hover) {
    .button-plus-option-value-happy:hover {
        @apply bg-hover-yellow-happy border-hover-yellow-happy;
    }
    .button-minus-option-value-happy:hover .svg-minus-happy {
        @apply stroke-bg-happy-light;
    }
    .button-minus-option-value-happy:hover {
        @apply bg-hover-yellow-happy border-hover-yellow-happy;
    }
}
</style>
